import { useQuery } from "@tanstack/react-query";
import {
  createRootRoute,
  createRoute,
  createRouter,
  type ErrorComponentProps,
  Link,
  Outlet,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { Icone, Logo, type NomeIcone } from "@vetbolso/ui";
import { api, ErroApi, ler, queryClient, sair } from "./api.ts";
import { BuscaFato, BuscaFichas, BuscaLogin, BuscaNovaFicha } from "./buscas.ts";
import { PaginaCompras } from "./paginas/compras.tsx";
import { PaginaFatoEditar } from "./paginas/fato.tsx";
import { PaginaFicha, PaginaNovaFicha } from "./paginas/ficha.tsx";
import { PaginaFichas } from "./paginas/fichas.tsx";
import { PaginaFonte, PaginaFontes } from "./paginas/fontes.tsx";
import { PaginaLogin } from "./paginas/login.tsx";
import { PaginaPainel } from "./paginas/painel.tsx";
import { PaginaPublicar } from "./paginas/publicar.tsx";
import { PaginaUsuario, PaginaUsuarios } from "./paginas/usuarios.tsx";
import { Botao } from "./ui.tsx";

export const consultaEu = { queryKey: ["eu"], queryFn: () => ler(api.eu.$get()) };
export const useEu = () => useQuery(consultaEu).data;

const NAV: { para: string; rotulo: string; icone: NomeIcone; papeis?: string[] }[] = [
  { para: "/", rotulo: "Painel", icone: "home" },
  { para: "/fichas", rotulo: "Fichas", icone: "file" },
  { para: "/fontes", rotulo: "Fontes e fatos", icone: "library" },
  { para: "/publicar", rotulo: "Publicação", icone: "route" },
  { para: "/usuarios", rotulo: "Usuários", icone: "grid", papeis: ["admin"] },
  { para: "/compras", rotulo: "Compras", icone: "store", papeis: ["admin"] },
];

function Casca() {
  const eu = useEu();
  const navegar = useNavigate();
  return (
    <div className="grid min-h-dvh grid-cols-1 grid-rows-[auto_1fr] md:grid-cols-[220px_minmax(0,1fr)] md:grid-rows-1">
      <aside className="flex flex-col gap-3 border-b border-line bg-surface p-3 md:sticky md:top-0 md:h-dvh md:gap-4 md:border-r md:border-b-0 md:p-4">
        <Link to="/" className="flex min-h-9 items-center gap-2 self-start">
          <Logo tamanho={30} />
          <span className="text-base font-bold text-ink">
            Vet<span className="text-primary">Bolso</span> <span className="font-semibold text-muted">admin</span>
          </span>
        </Link>
        <nav className="flex flex-row flex-wrap gap-1 md:flex-col" aria-label="Seções">
          {NAV.filter((n) => !n.papeis || (eu && n.papeis.includes(eu.papel))).map((n) => (
            <Link
              key={n.para}
              to={n.para}
              activeOptions={{ exact: n.para === "/" }}
              className="flex min-h-9 shrink-0 items-center gap-2 whitespace-nowrap rounded-lg px-2.5 py-1.5 text-sm font-medium text-muted hover:bg-surface-2 hover:text-ink [&.active]:bg-primary-soft [&.active]:text-primary-strong"
            >
              <Icone nome={n.icone} className="sm" />
              {n.rotulo}
            </Link>
          ))}
        </nav>
        {eu ? (
          <div className="flex items-center gap-3 border-t border-line pt-3 text-xs md:mt-auto md:flex-col md:items-stretch md:gap-1">
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate font-semibold text-ink">{eu.nome}</span>
              <span className="truncate text-muted">
                {eu.papel}
                {eu.crmv ? ` · CRMV-${eu.uf} ${eu.crmv}` : ""}
              </span>
            </div>
            <Botao
              variante="fantasma"
              className="md:-ml-3 md:justify-start"
              onClick={async () => {
                await sair().catch(() => {});
                queryClient.clear();
                navegar({ to: "/login" });
              }}
            >
              Sair
            </Botao>
          </div>
        ) : null}
      </aside>
      <main className="flex min-w-0 flex-col gap-5 p-4 md:p-6">
        <Outlet />
      </main>
    </div>
  );
}

const raiz = createRootRoute({ component: Outlet });
const login = createRoute({
  getParentRoute: () => raiz,
  path: "/login",
  validateSearch: BuscaLogin,
  component: PaginaLogin,
});
const area = createRoute({
  getParentRoute: () => raiz,
  id: "area",
  component: Casca,
  beforeLoad: async () => {
    try {
      await queryClient.ensureQueryData(consultaEu);
    } catch (e) {
      if (e instanceof ErroApi && (e.status === 401 || e.status === 403))
        throw redirect({ to: "/login", search: { volta: location.pathname } as never });
      throw e;
    }
  },
});
const r = <P extends string>(path: P, component: () => React.ReactNode) => createRoute({ getParentRoute: () => area, path, component });

const arvore = raiz.addChildren([
  login,
  area.addChildren([
    r("/", PaginaPainel),
    createRoute({ getParentRoute: () => area, path: "/fichas", validateSearch: BuscaFichas, component: PaginaFichas }),
    createRoute({ getParentRoute: () => area, path: "/fichas/nova", validateSearch: BuscaNovaFicha, component: PaginaNovaFicha }),
    r("/fichas/$id", PaginaFicha),
    r("/fontes", PaginaFontes),
    r("/fontes/$id", PaginaFonte),
    createRoute({ getParentRoute: () => area, path: "/fatos/$id", validateSearch: BuscaFato, component: PaginaFatoEditar }),
    r("/publicar", PaginaPublicar),
    r("/usuarios", PaginaUsuarios),
    r("/usuarios/$id", PaginaUsuario),
    r("/compras", PaginaCompras),
  ]),
]);

function ErroPagina({ error }: ErrorComponentProps) {
  return (
    <div className="mx-auto flex w-full max-w-lg flex-col gap-3 p-6">
      <h1 className="text-xl font-bold">Algo deu errado</h1>
      <p className="text-sm text-muted">A página não pôde ser exibida. Tente de novo; se continuar, avise a equipe técnica.</p>
      <p className="break-words rounded-lg bg-surface-2 px-3 py-2 font-mono text-xs text-muted">
        {error instanceof Error ? error.message : String(error)}
      </p>
      <div className="flex flex-wrap gap-2">
        <Botao variante="primario" onClick={() => window.location.reload()}>
          Recarregar
        </Botao>
        <Link to="/" className="inline-flex min-h-9 items-center rounded-lg px-3 text-sm font-semibold text-primary hover:bg-surface-2">
          Ir para o painel
        </Link>
      </div>
    </div>
  );
}

const NaoEncontrada = () => (
  <div className="mx-auto flex w-full max-w-lg flex-col gap-3 p-6">
    <h1 className="text-xl font-bold">Página não encontrada</h1>
    <Link to="/" className="text-sm font-semibold text-primary">
      Voltar ao painel
    </Link>
  </div>
);

export const router = createRouter({
  routeTree: arvore,
  defaultPreload: "intent",
  defaultErrorComponent: ErroPagina,
  defaultNotFoundComponent: NaoEncontrada,
});
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
